import React, {useCallback, useEffect, useMemo, useRef} from "react";
import PropTypes from "prop-types";
import InputDiscount from "../../../components/common/form/input-discount";
import {
    formatDiscountLabel,
    normalizeDiscountList,
    stringifyDiscountList,
} from "./document-body-utils";

const ReadOnlyDiscountSummary = ({value, t}) => {
    const normalized = normalizeDiscountList(value);
    if (!normalized || normalized.length === 0) {
        return (
            <span className="text-sm truncate text-gray-500 italic">
                {t("app.quotations.no_discount", {defaultValue: "Nessuno sconto"})}
            </span>
        );
    }
    return (
        <span className="text-sm truncate text-gray-900">
            {normalized.map((discount) => formatDiscountLabel(discount)).join(" + ")}
        </span>
    );
};

const EditableDiscountListInput = ({value, onCommit, suffix, t, showDiscountIndicator}) => {
    const parsedValue = useMemo(() => normalizeDiscountList(value), [value]);
    const initialSerializedRef = useRef(JSON.stringify(parsedValue));
    const skipInitialRef = useRef(true);

    useEffect(() => {
        initialSerializedRef.current = JSON.stringify(parsedValue);
        skipInitialRef.current = true;
    }, [parsedValue]);

    const handleChangeValues = useCallback((payload) => {
        const normalized = normalizeDiscountList(payload?.discount);
        const serialized = JSON.stringify(normalized);
        if (skipInitialRef.current && serialized === initialSerializedRef.current) {
            skipInitialRef.current = false;
            return;
        }
        skipInitialRef.current = false;
        if (typeof onCommit === "function") {
            onCommit(stringifyDiscountList(normalized));
        }
    }, [onCommit]);

    return (
        <div className="flex items-center justify-center w-full">
            <InputDiscount
                value={0}
                onChange={() => {}}
                onChangeValues={handleChangeValues}
                discountList={parsedValue}
                isShowLabel={false}
                isFullW={false}
                showPriceInput={false}
                showDiscountSummary={false}
                showDiscountIndicator={showDiscountIndicator}
                containerClass="flex items-center"
                discountButtonClass="flex items-center justify-center px-2 py-1"
                discountBoxClass="group p-1 flex bg-green-600 hover:bg-green-700 rounded"
                suffix={suffix}
            />
        </div>
    );
};

const DocumentBodyEditableDiscountListInput = (props) => (
    props.isEditable
        ? (
            <EditableDiscountListInput
                value={props.value}
                onCommit={props.onCommit}
                suffix={props.suffix}
                t={props.t}
                showDiscountIndicator={true}
            />
        )
        : (
            <ReadOnlyDiscountSummary
                value={props.value}
                t={props.t}
            />
        )
);

DocumentBodyEditableDiscountListInput.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    ]),
    onCommit: PropTypes.func,
    suffix: PropTypes.string,
    t: PropTypes.func.isRequired,
    isEditable: PropTypes.bool,
    showDiscountIndicator: PropTypes.bool,
};

DocumentBodyEditableDiscountListInput.defaultProps = {
    value: [],
    onCommit: undefined,
    suffix: "",
    isEditable: true,
    showDiscountIndicator: false,
};

export default DocumentBodyEditableDiscountListInput;
